import React, {useEffect, useMemo, useState} from "react";
import {RegistrationApiGet, useGetRegListQuery} from "../../registration/reg_api";
import {useDispatch, useSelector} from "react-redux";
import {RootState} from "../../../store";
import {GridState} from "../../../libs/grid_state_reducers";
import {
    FilterItem,
    RegFilters,
    setColumns,
    setDensity,
    setEndTime,
    setFilter,
    setMachine,
    setOperation,
    setPaginationModel,
    setPartner,
    setProduct,
    setSort,
    setStartTime,
    setUser,
    setWorker,
    setWorkOrder,
} from "./reg_grid_slice";

import {Box, Link} from "@mui/material";
import {DataGridPro, GridColDef} from "@mui/x-data-grid-pro";
import OperationFilter from "../../operation/OperationFilter";
import DateTimeFilter from "../../../components/DateTimeFilter";
import MachineFilter from "../../machines/MachineFilter";
import PersonFilter from "../../people/PersonFilter";
import UserFilter from "../../users/UserFilter";
import {datetimeFormat} from "../../../lang/date-formats";
import ProductFilter from "../../products/ProductFilter";
import WorkOrderFilter from "../../workOrders/WorkOrderFilter";
import PartnerFilter from "../../partners/PartnerFilter";
import AnalyticsGridToolbar from "../AnalyticsGridToolbar";
import AnalyticsGridFooter from "../AnalyticsGridFooter";
import FilterSwitcher from "../FilterSwitcher";
import {AnaliticsFilterToolbar} from "../../../components/styled";

export default () => {

    const dispatch = useDispatch();
    const gridState = useSelector<RootState, GridState & RegFilters>((state) => state.regGrid)
    const {data, isFetching, error} = useGetRegListQuery(gridState);

    const [hours, setHours] = useState(0)
    useEffect(() => {
        if (data) {
            let sum = 0
            data.result.forEach(row => {
                sum += row.ElapsedTime
            })
            setHours(sum)
            return
        }
        setHours(0)
    }, [data])

    const columns = useMemo<GridColDef<RegistrationApiGet>[]>(() => {
        return [
            {
                field: 'profile',
                headerName: 'Uporabnik',
                renderCell: (params) => {
                    return <Link href={`/reg/${params.row.id}`}>{params.row?.profile?.fullName}</Link>
                },
                width: 120
            },
            {
                field: 'Status',
                headerName: 'Status',
                width: 100,
            },
            {
                field: 'StartTime',
                type: 'dateTime',
                valueGetter: (value) => value && new Date(value),
                headerName: 'Začetek',
                valueFormatter: (val?: Date) => val?.toLocaleString("sl", datetimeFormat),
                width: 150
            },
            {
                field: 'EndTime',
                valueGetter: (value) => value ? new Date(value) : undefined,
                valueFormatter: (val?: Date) => val?.toLocaleString("sl", datetimeFormat) ?? "",
                type: 'dateTime',
                headerName: 'Konec',
                width: 150
            },
            {
                field: 'WorkOrder',
                valueGetter: (value, row) => row.workOrder?.number,
                headerName: 'DN',
                width: 150
            },
            {
                field: 'buyer',
                valueGetter: (value, row) => row.workOrder?.buyerName,
                headerName: 'Kupec',
                width: 150
            },
            {
                field: 'product',
                valueGetter: (value, row) => row.product?.Name,
                headerName: 'Artikel',
                width: 150
            },
            {
                field: 'productCode',
                valueGetter: (value, row) => row.product?.Code,
                headerName: 'Šifra',
                width: 150
            },
            {
                field: "operation",
                valueGetter: (value, row) => row.workTask?.Name,
                headerName: 'Operacija',
                width: 150
            },

            {
                field: "worker",
                valueGetter: (value, row) => row.worker?.lastName + " " + row.worker?.firstName,

                headerName: 'Delavec',
                width: 150
            },

            {
                field: "machine",
                valueGetter: (value, row) => row.machine?.Name,
                headerName: 'Stroj',
                width: 150
            },
            {
                field: "ElapsedTime",
                headerName: 'Čas',
                valueFormatter: (val) => {
                    const hours = Math.trunc(val / 3600);
                    const minutes = Math.round((val - hours * 3600) / 60);
                    return hours + ":" + (`0${minutes}`).slice(-2)
                }
            },
            {
                field: "ElapsedMinutes",
                headerName: 'Čas (minute)',
                valueGetter: (value, row) => row.ElapsedTime,
                valueFormatter: (val) => {
                    return  Math.round(val / 60);
                }
            }
        ];
    }, []);

    useEffect(() => {
        dispatch(setColumns({
            CreatedBy: true,
            Status: true,
            StartTime: true,
            EndTime: true,
            ElapsedTime: true,
        }))
    }, [])

    const availableFilters = useMemo(() => new Set<FilterItem>(["machine", "operation", "worker", "workOrder", "user", "partner", "product"]), [])
    const [seq, setSeq] = useState(0)
    const enabledFilters: Set<FilterItem> = new Set<FilterItem>(JSON.parse(localStorage.getItem("filters_reggrid") || "[]"));
    const activeFilters = new Set<FilterItem>()
    if (gridState.operation.length) activeFilters.add("operation");
    if (gridState.machine.length) activeFilters.add("machine");
    if (gridState.workOrder.length) activeFilters.add("workOrder");
    if (gridState.worker.length) activeFilters.add("worker");
    if (gridState.user.length) activeFilters.add("user");
    if (gridState.partner.length) activeFilters.add("partner");
    if (gridState.product.length) activeFilters.add("product");
    const showFilter = (filter: FilterItem) => activeFilters.has(filter) || enabledFilters.has(filter)


    return <Box
        sx={{pb: "1em", pt: "1em"}}>
        <Box sx={{
            display: "flex",
            maxWidth: "100%"
        }}>
            <AnaliticsFilterToolbar>

                <DateTimeFilter label="Začetek" value={gridState.startTime}
                                onChange={(val) => dispatch(setStartTime(val))}/>
                <DateTimeFilter label="Konec" value={gridState.endTime} onChange={(val) => dispatch(setEndTime(val))}/>
                {showFilter("operation") &&
                    <OperationFilter value={gridState.operation} onChange={(val) => dispatch(setOperation(val))}/>}
                {showFilter("machine") &&
                    <MachineFilter
                        value={gridState.machine}
                        onChange={(val) => dispatch(setMachine(val))}/>}
                {showFilter("worker") &&
                    <PersonFilter
                        value={gridState.worker}
                        onChange={(val) => dispatch(setWorker(val))}/>}
                {showFilter("user") && <UserFilter
                    value={gridState.user}
                    onChange={(val) => dispatch(setUser(val))}/>}
                {showFilter("product") &&
                    <ProductFilter
                        value={gridState.product}
                        onChange={(val) => dispatch(setProduct(val))}/>}
                {showFilter("workOrder") &&
                    <WorkOrderFilter
                        value={gridState.workOrder}
                        onChange={(val) => dispatch(setWorkOrder(val))}/>}
                {showFilter("partner") &&
                    <PartnerFilter
                        value={gridState.partner}
                        onChange={(val) => dispatch(setPartner(val))}/>}
            </AnaliticsFilterToolbar>
            <Box>
                <FilterSwitcher
                    availableFilters={availableFilters}
                    onChange={(vals) => {
                        console.log(vals)
                        localStorage.setItem("filters_reggrid", JSON.stringify(Array.from(vals)))
                        setSeq(seq + 1)
                    }}
                    enabled={enabledFilters}
                    active={activeFilters}
                />
            </Box>
        </Box>

        <main style={{flexDirection: "column", display: "flex"}}>
            <DataGridPro
                slots={{
                    toolbar: AnalyticsGridToolbar,
                    footer: () => AnalyticsGridFooter(hours)
                }}
                columns={columns}
                paginationModel={gridState.paginationModel}
                onPaginationModelChange={model => dispatch(setPaginationModel(model))}
                pageSizeOptions={[50, 100, 500, 5000]}
                density={gridState.density}
                onDensityChange={(density) => dispatch(setDensity(density))}
                pagination
                filterMode="server"
                sortModel={gridState.sort}
                onSortModelChange={(model) => dispatch(setSort(model))}
                onFilterModelChange={(model) => {
                    dispatch(setFilter(model))
                }}
                columnVisibilityModel={gridState.columns}
                loading={isFetching}
                onColumnVisibilityModelChange={(e) => dispatch(setColumns(e))}
                rows={data?.result || []}
            />
        </main>

    </Box>;
}
